import { useEffect, useState } from "react";
import PageShell from "@/components/PageShell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate, Link } from "react-router-dom";
import { SupportThread, CAT_LABELS } from "@/components/SupportThread";
import { Loader2, LifeBuoy, ArrowLeft, Inbox } from "lucide-react";

const FILTERS = [
  { id: "", label: "All" },
  { id: "open", label: "Open" },
  { id: "answered", label: "Answered" },
  { id: "closed", label: "Resolved" },
];

export default function AdminSupport() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/support/admin/tickets${filter ? `?status=${filter}` : ""}`)
      .then((r) => { setTickets(r.data.tickets); setCounts(r.data.counts); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (user && user.role === "admin") load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user, filter]);

  if (user === null) return <PageShell><div className="p-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div></PageShell>;
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <PageShell>
      <div className="max-w-7xl mx-auto px-6 py-12" data-testid="admin-support-page">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <span className="text-xs uppercase tracking-widest font-mono text-primary flex items-center gap-2"><LifeBuoy className="w-4 h-4" /> Admin · Support</span>
            <h1 className="font-serif text-5xl mt-2">Support Inbox</h1>
            <p className="text-sm text-muted-foreground mt-2">
              {counts.total || 0} tickets · <span className="text-primary">{counts.unread || 0} need a reply</span>
            </p>
          </div>
          <Link to="/admin" data-testid="admin-support-back"
            className="flex items-center gap-2 border border-border px-3 py-2 rounded-sm text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors">
            <ArrowLeft className="w-4 h-4" /> Content Editor
          </Link>
        </div>

        <div className="flex gap-2 mb-6" data-testid="admin-support-filters">
          {FILTERS.map((f) => (
            <button key={f.id || "all"} onClick={() => { setFilter(f.id); setSelected(null); }} data-testid={`admin-support-filter-${f.id || "all"}`}
              className={`px-4 py-2 text-xs uppercase tracking-widest font-mono border rounded-sm transition-colors ${filter === f.id ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:border-primary"}`}>
              {f.label}{f.id && counts[f.id] != null ? ` (${counts[f.id]})` : ""}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-[360px_1fr] gap-px bg-border border border-border rounded-md overflow-hidden">
          <aside className="bg-card lg:max-h-[72vh] lg:overflow-y-auto" data-testid="admin-support-list">
            {loading ? (
              <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
            ) : tickets.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground text-sm"><Inbox className="w-8 h-8 mx-auto mb-3 opacity-60" />No tickets here.</div>
            ) : tickets.map((t) => (
              <button key={t.id} onClick={() => setSelected(t.id)} data-testid={`admin-support-item-${t.id}`}
                className={`w-full text-left px-4 py-3.5 border-b border-border transition-colors ${selected === t.id ? "bg-muted" : "hover:bg-muted/50"}`}>
                <div className="flex items-center gap-2">
                  {t.unread_admin && <span className="w-2 h-2 rounded-full bg-primary shrink-0" title="Awaiting reply" />}
                  <span className="font-medium text-sm truncate flex-1">{t.subject}</span>
                  <span className={`text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded-full border shrink-0 ${t.status === "answered" ? "text-emerald-400 border-emerald-500/40" : t.status === "closed" ? "text-muted-foreground border-border" : "text-primary border-primary/50"}`}>{t.status}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1 truncate">{t.user_name} · {CAT_LABELS[t.category] || "Other"}</div>
              </button>
            ))}
          </aside>

          <section className="bg-card p-6 lg:h-[72vh]">
            {selected ? (
              <SupportThread ticketId={selected} isAdmin onChanged={load} />
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground text-sm">Select a ticket to view the conversation.</div>
            )}
          </section>
        </div>
      </div>
    </PageShell>
  );
}
